import { useContext, useState, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import _ from "lodash"
import PropTypes from "prop-types"
import { useQuery, useLazyQuery } from "@apollo/client"
import Filters from "./components/filters/filters"
import { DateTime } from "luxon"
// Comps
import ListHeader from "./components/ListHeader"

// Context
import { UserContext } from "../../context/userContext"

// Queries
import { GET_TEMPLATE, LIST_BY_DATE } from "./queries"

// Loaders
import { LoaderMedium } from "../Loaders"

// Prime React
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { SplitButton } from "primereact/splitbutton"
import { Toast } from "primereact/toast"

// Helpers
import { handleOptsForFilter } from "../DataTable/helpers/index"
import { DATE_SUBTYPES, transformDate } from "../../helpers/dateNormalization"

// Styles
import styles from "../../styles/ListPageView.module.css"
import { navigateWithOrigin } from "../../helpers/navigation"

function ListPageView(props) {
  const [payload, setPayload] = useState([])
  const [typesFilter, setTypesFilter] = useState([])
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const toastRef = useRef(null)
  const [totalCount, setTotalCount] = useState(0)
  const [locationFilter, setLocationFilter] = useState([])
  const [staffFilter, setStaffFilter] = useState([])
  const [programFilter, setProgramFilter] = useState([])
  const [volunteerLogExists, setVolunteerLogExists] = useState(false)
  // Dynamic filters
  const [dynamicFilters, setDynamicFilters] = useState(props.extraFilters || [])
  const [loading, setLoading] = useState(false)
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()
  const idTemplate = props.idTemplate
  const idBlocks = props.idBlocks || []
  const columns = props.columns || []

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)

      const currentDate = DateTime.now()
      const defaultStartDate = currentDate.startOf("month").toJSDate()
      const defaultEndDate = currentDate.endOf("month").toJSDate()

      setStartDate(defaultStartDate)
      setEndDate(defaultEndDate)

      try {
        await refetch({
          organization_id: userCtx.session.user.organization_id,
          idTemplate: idTemplate,
          idBlocks: idBlocks,
          numberOfDays: currentDate.daysInMonth,
          dateField: `values.${columns[0].field}`,
          activityField: columns[0].block,
          startDate: defaultStartDate.toISOString().split("T")[0],
          endDate: defaultEndDate.toISOString().split("T")[0],
        })
      } catch (error) {
        console.error("Error fetching data:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleLocationFilterChange = (values) => {
    setLocationFilter([...values])
  }

  const handleStaffFilterChange = (values) => {
    setStaffFilter([...values])
  }

  const handleProgramFilterChange = (values) => {
    setProgramFilter([...values])
  }

  const handleDataTransform = (data) => {
    let groupedTemplates = _.map(data, (template) => {
      const blocks = _.groupBy(template, "block_name")
      return blocks
    })

    const localIdBlocks = [...idBlocks]

    // Check if data contains all required keys based on idBlocks
    for (const object of groupedTemplates) {
      const missingKeys = _.difference(localIdBlocks, _.keys(object))
      if (!_.isEmpty(missingKeys)) {
        console.log(`Object is missing keys: ${_.join(missingKeys, ", ")}`)
        _.pullAll(localIdBlocks, missingKeys)
      } else {
        console.log(
          `Object contains all required keys: ${_.join(localIdBlocks, ", ")}`
        )
      }
    }

    function filterByParticipantId(data, participantId) {
      return _.filter(data, (item) => {
        const forms = _.flatMap(item)
        return _.some(
          forms,
          (form) => _.get(form, "participant_id") === participantId
        )
      })
    }

    // Check if filtering by participant_id is enabled
    const filterByParticipantIdEnabled = props.filterByParticipantId || false
    if (filterByParticipantIdEnabled) {
      const participantId = userCtx.participant_id || null
      if (!participantId) {
        console.log("Participant ID is missing")
      } else {
        groupedTemplates = filterByParticipantId(
          groupedTemplates,
          participantId
        )
      }
    }

    const handleLabels = (field) => _.join(_.map(field, "label"), ", ")

    const handleLengthInMins = (hours, minutes) =>
      `${Number(hours) * 60 + Number(minutes)} minutes`

    // Check if any template contains the block 'activity__volunteer_log'
    const volunteerLogPresent = groupedTemplates.some(
      (template) => template["activity__volunteer_log"]
    )
    setVolunteerLogExists(volunteerLogPresent)

    return _.map(groupedTemplates, (template) => {
      let newData = _.reduce(
        columns,
        (acc, col) => {
          if (_.isUndefined(template[col.block])) {
            console.warn(`Values for block "${col.block}" is wrong or missing`)
            return acc
          }

          const templateValues = template[col.block][0].values || {}
          const fieldName = col.field.split("-")[0]
          const fieldValue = templateValues[fieldName]

          switch (col.type) {
            case "string":
            case "activity_type": {
              acc[col.field] = fieldValue || ""
              if (col.type === "activity_type") {
                setTypesFilter(_.uniq([...typesFilter, fieldValue]).sort())
              }
              break
            }
            case "type_curriculum": {
              acc[col.field] = fieldValue.substring(0, fieldValue.indexOf("|"))
              break
            }
            case "week_curriculum": {
              acc[col.field] = fieldValue.slice(fieldValue.indexOf("|") + 1)
              break
            }
            case "integer": {
              acc[col.field] = Number(fieldValue) || 0
              break
            }
            case "date": {
              acc[col.field] = fieldValue
                ? transformDate(fieldValue, "date", col.subtype ?? DATE_SUBTYPES.DATE)
                : ""
              break
            }
            case "program": {
              acc[col.field] = handleLabels(fieldValue)

              break
            }
            case "location": {
              acc[col.field] = handleLabels(fieldValue)
              break
            }
            case "staff": {
              acc[col.field] = template[col.block][0][fieldName]
              acc.staff_id = template[col.block][0].staff_id // Ensure the staff ID is assigned

              break
            }
            case "lenght":
              {
                const [hoursField, minutesField] = _.split(fieldName, "|")
                const hours = templateValues[hoursField] || 0
                const minutes = templateValues[minutesField] || 0
                acc[col.type] = handleLengthInMins(hours, minutes)
              }
              break
            case "attendees": {
              const [attendance, distributed] = _.split(fieldName, "|")
              acc[col.type] =
                attendance === ""
                  ? templateValues[distributed]
                  : templateValues[attendance]
              break
            }
            case "concatenation": {
              acc[col.type] = _.chain(fieldName)
                .split("|")
                .map((key) => _.get(templateValues, key))
                .join(" ")
                .value()
              break
            }
            default:
              return acc
          }
          return acc
        },
        {}
      )

      // idStamp for 1st block is equal to any block
      const idStamp = template[localIdBlocks[0]][0]?.idStamp || ""

      // Add the "Edit" column
      newData.edit = (
        <SplitButton
          label="View"
          onClick={async () => {
            if (!filterByParticipantIdEnabled) {
              await userCtx.changeParticipant(null)
            }
            navigateWithOrigin(navigate, `/form/view/${idTemplate}/${idStamp}`)
          }}
          model={[
            {
              label: "Edit",
              command: () => {
                if (
                  !["Inactive Discharged", "Inactive Referrals"].includes(
                    userCtx.participant_membership_status
                  )
                ) {
                  navigate(`/form/${idTemplate}/1/${idStamp}`)
                }
              },
              disabled: ["Inactive Discharged", "Inactive Referrals"].includes(
                userCtx.participant_membership_status
              ),
            },
          ]}
        />
      )

      return newData
    })
  }

  const handleOnCompleted = (data) => {
    try {
      const { data: fetchData, error, rowCount } = data.data
      if (error) {
        console.log("Error fetching data:", error.message)
        setPayload([])
        // Display the error message using a toast notification
        if (toastRef.current) {
          toastRef.current.show({
            severity: "error",
            summary: "Error",
            detail: error.message,
            life: 5000,
          })
        }
        return
      }

      const parsedData = JSON.parse(fetchData)
      const transformedData = handleDataTransform(parsedData)
      setPayload(transformedData)
      setTotalCount(rowCount) // Store the total count in the state
    } catch (error) {
      console.log("Error parsing data onCompleted: ", error)
      setPayload([])
    }
  }

  const handleQueryError = (error) => {
    console.log("Error fetching data: ", error)
    setPayload([])
  }

  // Initial query
  const { loading: loadingInit, refetch } = useQuery(GET_TEMPLATE, {
    variables: {
      organization_id: userCtx.session.user.organization_id,
      idTemplate: idTemplate,
      idBlocks: idBlocks,
      numberOfDays: 90,
      dateField: `values.${columns[0].field}`,
      activityField: columns[0].block,
    },
    fetchPolicy: "network-only",
    skip: !userCtx.session.user.staff_id,
    onCompleted: handleOnCompleted,
    onError: handleQueryError,
  })

  // We update the dynamic filters only when the initial data is ready
  useEffect(() => {
    if (!loadingInit && payload.length > 0) {
      const updatedFilters = dynamicFilters.map((filter) => {
        if (filter.type === "select" && filter.id === "activity_type_hc") {
          const options = handleOptsForFilter(payload, filter.id)
          return { ...filter, options }
        }
        return filter
      })

      setDynamicFilters(updatedFilters)
    }
  }, [loadingInit, payload])

  const [getList, { loading: loadingPager }] = useLazyQuery(LIST_BY_DATE, {
    fetchPolicy: "network-only",
    onCompleted: handleOnCompleted,
    onError: handleQueryError,
  })

  const COLUMN_TYPES = {
    DATE: "date",
    ACTIVITY_TYPE: "activity_type",
    // Add other column types here
  }
  const handleFilterData = () => {
    if (startDate && endDate) {
      getList({
        variables: {
          organization_id: userCtx.session.user.organization_id,
          idTemplate: idTemplate,
          idBlocks: idBlocks,
          numberOfDays: 90,
          dateField: `values.${columns[0].field}`,
          activityField: columns[0].block,
          startDate: startDate.toISOString().split("T")[0],
          endDate: endDate.toISOString().split("T")[0],
          locationFilter: locationFilter,
          staffFilter: staffFilter, // Use the stored staff ID
          programFilter: programFilter,
        },
      })
    }
  }
  const createColumn = ({ field, header, type, sortable = true }) => {
    let additionalProps = {}

    switch (type) {
      case COLUMN_TYPES.DATE:
        {
          const formatDate = (value) => {
            const dateObject = new Date(value)
            return dateObject.toLocaleDateString("en-US", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          }
          const dateBodyTemplate = (rowData) => formatDate(rowData[field])
          additionalProps = {
            filterField: field,
            dataType: "date",
            filter: false,
            body: dateBodyTemplate,
          }
        }
        break
      // case COLUMN_TYPES.ACTIVITY_TYPE:
      //   {
      //     const libraryRowFilterTemplate = (options) => (
      //       <Dropdown
      //         value={options.value}
      //         options={typesFilter}
      //         onChange={(e) => options.filterApplyCallback(e.value)}
      //         placeholder="Select a Type"
      //         className="p-column-filter"
      //         showClear
      //       />
      //     )
      //     additionalProps = {
      //       filter: true,
      //       filterPlaceholder: "Search by Activity Type",
      //       filterElement: libraryRowFilterTemplate,
      //       showFilterMenuOptions: false,
      //     }
      //   }
      //   break
      // Handle other column types here
      default:
        break
    }

    return (
      <Column
        field={field}
        header={header}
        key={field}
        sortable={sortable}
        {...additionalProps}
      />
    )
  }
  const extractOptions = (data, field, delimiter = null) => {
    const options = data.reduce((acc, item) => {
      const values = item[field]
      if (values) {
        const splitValues = delimiter
          ? values.split(delimiter).map((value) => value.trim())
          : [values]
        splitValues.forEach((value) => {
          if (!acc.includes(value)) {
            acc.push(value)
          }
        })
      }
      return acc
    }, [])
    return options.filter((option) => option !== undefined && option !== "")
  }

  const locationOptions = extractOptions(payload, "activity__location_ids", ",")
  const staffOptions = extractOptions(payload, "staff_full_name").map(
    (fullName) => {
      const staffMember = payload.find(
        (item) => item.staff_full_name === fullName
      )
      return {
        label: fullName,
        value: staffMember.staff_id, // Assuming the staff ID is available in the payload
      }
    }
  )

  const programOptions = extractOptions(
    payload,
    "activity__program_identity_ids",
    ","
  )
  const handleColumns = () => {
    let updatedColumns = _.map(columns, (column) => {
      const isComposite = _.includes(column.field, "|")
      // Use the 'type' as the field if it's a composite
      const field = isComposite ? column.type : column.field

      // Check if the column belongs to the activity__volunteer_log block
      const isVolunteerLogColumn =
        column.block === "activity__volunteer_log" ||
        column.field === "activity_length_hours|activity_length_minutes"

      // Skip rendering the column if activity__volunteer_log is null
      if (isVolunteerLogColumn && !volunteerLogExists) {
        return null
      }

      return createColumn({ ...column, field })
    })

    // Filter out the null columns
    updatedColumns = _.filter(updatedColumns, (column) => column !== null)

    // Append the "Edit" column
    updatedColumns.push(
      createColumn({
        field: "edit",
        header: "Action",
        type: "edit",
        sortable: false,
      })
    )

    return updatedColumns
  }
  const handleClearSelection = () => {
    const currentDate = DateTime.now()
    const defaultStartDate = currentDate.startOf("month").toJSDate()
    const defaultEndDate = currentDate.endOf("month").toJSDate()
    if (props?.clearAllFilters) {
      props.clearAllFilters()
    }

    if (setDynamicFilters) {
      setDynamicFilters((prevFilters) =>
        prevFilters.map((filter) => ({
          ...filter,
          value: filter.type === "select" ? "" : "",
        }))
      )
    }
    setStartDate(defaultStartDate)
    setEndDate(defaultEndDate)
    setLocationFilter([])
    setStaffFilter([])
    setProgramFilter([])

    refetch({
      organization_id: userCtx.session.user.organization_id,
      idTemplate: idTemplate,
      idBlocks: idBlocks,
      numberOfDays: currentDate.daysInMonth,
      dateField: `values.${columns[0].field}`,
      activityField: columns[0].block,
      startDate: defaultStartDate.toISOString().split("T")[0],
      endDate: defaultEndDate.toISOString().split("T")[0],
    }).then(() => { })
  }

  if (loadingInit || loadingPager || loading) {
    return <LoaderMedium />
  }
  const getDefaultSortField = (payload) => {
    if (payload && payload.length > 0) {
      const firstItem = payload[0]
      const firstKey = Object.keys(firstItem)[0]
      return firstKey
    }
    return null
  }
  const defaultSortField = getDefaultSortField(payload) || "activity_date"
  return (
    <>
      <ListHeader idTemplate={idTemplate} />
      <Filters
        onFilterData={handleFilterData}
        onClearSelection={handleClearSelection}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        locationFilter={locationFilter}
        onLocationFilterChange={handleLocationFilterChange}
        staffFilter={staffFilter}
        onStaffFilterChange={handleStaffFilterChange}
        programFilter={programFilter}
        onProgramFilterChange={handleProgramFilterChange}
        locationOptions={locationOptions}
        staffOptions={staffOptions}
        dynamicFilters={dynamicFilters}
        setDynamicFilters={setDynamicFilters}
        programOptions={programOptions}
        disableFilter={props.disableFilter}
      />
      {loadingPager || loadingInit ? (
        <LoaderMedium />
      ) : (
        <DataTable
          value={payload}
          paginator
          rows={50}
          filters={props.filters}
          className={styles["data-table"]}
          sortField={defaultSortField}
          sortOrder={-1}
          footer={`Showing ${payload.length} of ${totalCount} results`}
        >
          {handleColumns()}
        </DataTable>
      )}
      <Toast ref={toastRef} />
    </>
  )
}

ListPageView.propTypes = {
  idTemplate: PropTypes.string.isRequired,
  idBlocks: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      block: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      subtype: PropTypes.oneOf([DATE_SUBTYPES.DATE, DATE_SUBTYPES.TIME, DATE_SUBTYPES.DATETIME]),
      filterByParticipantId: PropTypes.bool,
    }),
  ).isRequired,
}

export default ListPageView
